import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Save } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

/**
 * EditDailyUpdateModal — Admin / Project Manager only.
 *
 * Edits a previously-posted daily update via
 * PATCH /api/portal/projects/{slug}/updates/{updateId}. All form fields are
 * pre-filled from the existing record so the user can correct typos (e.g.
 * "None" under delays that was being rendered as "Delay 1" in emails)
 * without having to re-enter every field. Photo edits are out of scope —
 * the photo list is preserved untouched.
 */
export default function EditDailyUpdateModal({ slug, update, onClose, onSaved, isAdmin }) {
  const dialogRef = useRef(null);
  const [form, setForm] = useState({
    date: update?.date || "",
    work_completed: update?.work_completed || "",
    manpower: update?.manpower ?? "",
    subs_on_site: update?.subs_on_site || "",
    materials_delivered: update?.materials_delivered || "",
    delays: update?.delays || "",
    next_day_plan: update?.next_day_plan || "",
    progress_percent: update?.progress_percent ?? "",
    visibility: update?.visibility || "public",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Esc closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onBackdrop = (e) => {
    if (dialogRef.current && !dialogRef.current.contains(e.target) && !busy) onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        work_completed: form.work_completed,
        manpower: form.manpower === "" ? null : Number(form.manpower),
        subs_on_site: form.subs_on_site || null,
        materials_delivered: form.materials_delivered || null,
        delays: form.delays || null,
        next_day_plan: form.next_day_plan || null,
        progress_percent: form.progress_percent === "" ? null : Number(form.progress_percent),
        visibility: form.visibility,
      };
      // Date changes are admin-only — never send `date` for non-admins so
      // other field edits don't get rejected.
      if (isAdmin && form.date && form.date !== update?.date) payload.date = form.date;
      const { update: saved } = await portalApi.editUpdate(slug, update.id, payload);
      onSaved && onSaved(saved);
      onClose();
    } catch (ex) {
      setErr(ex.message || "Failed to save changes");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
      onMouseDown={onBackdrop}
      data-testid="portal-edit-update-modal"
    >
      <div
        ref={dialogRef}
        className="bg-[#FAF5EC] border border-[#6E1F2C]/20 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#6E1F2C]/15 bg-white">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Site Diary</p>
            <h2 className="font-serif-display text-xl text-[#1C110C]">Edit daily update</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="portal-edit-update-close"
            className="text-[#7A6B5F] hover:text-[#6E1F2C] p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">
                Date
                {!isAdmin && (
                  <span className="text-[#7A6B5F] ml-2 normal-case tracking-normal">(admin-only)</span>
                )}
              </span>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setField("date", e.target.value)}
                data-testid="portal-edit-update-date"
                disabled={!isAdmin}
                title={isAdmin ? "" : "Only admins can change the date of a daily update"}
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] disabled:opacity-60 disabled:cursor-not-allowed"
                required
              />
              {isAdmin && form.date !== update?.date && (
                <span className="text-[10px] text-[#A6822F]" data-testid="portal-edit-update-date-changed">
                  Date change will be recorded in the audit log.
                </span>
              )}
            </label>
            <label className="space-y-1">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Visibility</span>
              <select
                value={form.visibility}
                onChange={(e) => setField("visibility", e.target.value)}
                data-testid="portal-edit-update-visibility"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              >
                <option value="public">Public (everyone)</option>
                <option value="client">Client only</option>
                <option value="subcontractor">Subcontractors only</option>
                <option value="internal">Internal team only</option>
              </select>
            </label>
          </div>

          <label className="block space-y-1">
            <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Work completed</span>
            <textarea
              value={form.work_completed}
              onChange={(e) => setField("work_completed", e.target.value)}
              data-testid="portal-edit-update-work"
              rows={4}
              required
              className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] resize-none"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Manpower</span>
              <input
                type="number"
                min={0}
                value={form.manpower}
                onChange={(e) => setField("manpower", e.target.value)}
                data-testid="portal-edit-update-manpower"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Progress %</span>
              <input
                type="number"
                min={0}
                max={100}
                value={form.progress_percent}
                onChange={(e) => setField("progress_percent", e.target.value)}
                data-testid="portal-edit-update-progress"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              />
            </label>
            <label className="space-y-1 sm:col-span-2">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Subcontractors on site</span>
              <input
                type="text"
                value={form.subs_on_site}
                onChange={(e) => setField("subs_on_site", e.target.value)}
                data-testid="portal-edit-update-subs"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              />
            </label>
            <label className="space-y-1 sm:col-span-2">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Materials delivered</span>
              <input
                type="text"
                value={form.materials_delivered}
                onChange={(e) => setField("materials_delivered", e.target.value)}
                data-testid="portal-edit-update-materials"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              />
            </label>
            <label className="space-y-1 sm:col-span-2">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">
                Delays / issues
                <span className="text-[#7A6B5F] ml-2 normal-case tracking-normal">
                  (free text — leave blank or write &quot;None&quot; if there are no delays)
                </span>
              </span>
              <input
                type="text"
                value={form.delays}
                onChange={(e) => setField("delays", e.target.value)}
                placeholder="e.g. Pending NOC approval / Material delivery delayed 2 days / None"
                data-testid="portal-edit-update-delays"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              />
            </label>
            <label className="space-y-1 sm:col-span-2">
              <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Planned for tomorrow</span>
              <textarea
                value={form.next_day_plan}
                onChange={(e) => setField("next_day_plan", e.target.value)}
                data-testid="portal-edit-update-plan"
                rows={2}
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] resize-none"
              />
            </label>
          </div>

          {isAdmin && update?.date_change_log?.length > 0 && (
            <div className="border border-[#D4A963]/40 bg-white px-4 py-3" data-testid="portal-edit-update-date-audit">
              <p className="text-[9px] tracking-[0.26em] uppercase text-[#A6822F] mb-1.5">🔒 Date change history (admin-only)</p>
              <ul className="space-y-1">
                {update.date_change_log.map((e, i) => (
                  <li key={i} className="text-[11px] text-[#3E2A22]">
                    <span className="line-through text-[#7A6B5F]">{e.from}</span>
                    {" → "}
                    <strong>{e.to}</strong>
                    {" — "}{e.changed_by_name || "Admin"} · {e.changed_at ? new Date(e.changed_at).toLocaleString() : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {err && (
            <p className="text-xs text-[#A3141A]" data-testid="portal-edit-update-error">{err}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#6E1F2C]/10">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              data-testid="portal-edit-update-cancel"
              className="px-4 py-2 text-[11px] tracking-[0.26em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="portal-edit-update-save"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] hover:bg-[#56151F] text-[#FAF5EC] px-5 py-2 text-[11px] tracking-[0.26em] uppercase disabled:opacity-60"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
